// Chemin: C:\gestion-copro\src\types\courrier.types.ts
// Types pour la gestion des courriers sortants - Copro Protect
// Session 40 : création initiale - module Communications
// Session 40 : correction - ajout champ numero
// Session 50 : ajout RelanceManuelle pour notifications CS
// Session 52 : ajout champs SENTINELLE V2 (nature, reformulation, motsClesEngageants)
// Session 81 : correction VoteCourrier - ajout membreId, ip, juridiquementValide
// Session 92 : Classification 3 niveaux (VERT/ORANGE/ROUGE) - Gouvernance V2
// Session 126 : Ajout modifieParUtilisateur (Courrier + CourrierUpdateData)
// Session 143 : Ajout statut 'recu' (correspondance entrante archivée via SENTINELLE)
//               Ajout SensCourrier (entrant/sortant), sens, documentGedId, sourceCreation dans Courrier
// Session 160 : Ajout destinatairesCC (copie carbone) dans Courrier, CourrierCreateData, CourrierUpdateData
// Session 173 : DestinataireCourrier.type aligné sur TypeInterlocuteur (import) — suppression liste hardcodée
// Session 180 : Cycle de vie courrier — StatutCourrierAffiche calculé à l'affichage
//               (en_attente, delai_depasse, repondu, clos) sans migration Firestore

import { Timestamp } from 'firebase/firestore';
import { TypeInterlocuteur } from './interlocuteur.types';

// ============================================================================
// STATUTS ET TYPES
// ============================================================================

/**
 * Statut d'un courrier dans le workflow
 * 
 * brouillon       → L'admin rédige, peut modifier librement
 * en_validation   → Soumis aux membres CS, verrouillé (ROUGE uniquement)
 * valide          → Prêt à envoyer (VERT/ORANGE = direct, ROUGE = après vote)
 * envoye          → Envoyé via Gmail ou PDF généré
 * recu            → Correspondance entrante archivée via SENTINELLE (Session 143)
 * annule          → Annulé par l'admin avant validation
 * rejete          → Rejeté par vote des membres CS (ROUGE uniquement)
 */
export type StatutCourrier = 'brouillon' | 'en_validation' | 'valide' | 'envoye' | 'recu' | 'annule' | 'rejete';

/**
 * Sens d'une correspondance (Session 143)
 * entrant  → Reçu par le copropriétaire (archivé via SENTINELLE)
 * sortant  → Envoyé par le copropriétaire (créé via module Correspondances)
 */
export type SensCourrier = 'entrant' | 'sortant';

/**
 * Type de courrier
 */
export type TypeCourrier = 
  | 'mise_en_demeure'
  | 'relance'
  | 'demande_information'
  | 'notification'
  | 'reclamation'
  | 'convocation'
  | 'compte_rendu'
  | 'autre';

/**
 * Mode d'envoi du courrier
 */
export type ModeEnvoi = 'email' | 'postal' | 'lrar' | 'recommande_ar';

/**
 * Niveau de risque évalué par Sentinelle (analyse SCUD)
 */
export type NiveauRisque = 'faible' | 'moyen' | 'eleve';

/**
 * Nature de la communication - SENTINELLE V2
 * - courante : opérationnelle, peut être envoyée directement (si risque OK)
 * - engageante : engage le CS collectivement, vote obligatoire
 */
export type NatureCommunication = 'courante' | 'engageante';

/**
 * Classification 3 niveaux - Gouvernance V2 (Session 92)
 * 
 * vert   → Information, demande de document, relance simple
 *          Workflow : Coordinateur envoie seul + notification CS
 * orange → Réclamation, contestation, mise en demeure amiable
 *          Workflow : Coordinateur envoie seul + notification CS
 * rouge  → Engagement juridique, menace de procédure, courrier avocat
 *          Workflow : Vote CS requis (min. 1 membre doit approuver)
 */
export type NiveauClassification = 'vert' | 'orange' | 'rouge';

/**
 * Statut du vote d'un membre CS
 */
export type StatutVote = 'approuve' | 'modification' | 'rejete';

// ============================================================================
// STRUCTURES DE DONNÉES
// ============================================================================

/**
 * Destinataire d'un courrier
 * Session 173 : type aligné sur TypeInterlocuteur — source unique, zéro hardcoding
 */
export interface DestinataireCourrier {
  type: TypeInterlocuteur;
  interlocuteurId?: string;    // Référence vers la collection interlocuteurs
  nom: string;
  adresse?: string;
  email?: string;
}

/**
 * Vote d'un membre CS sur un courrier
 * Session 81 : ajout membreId, ip, juridiquementValide pour traçabilité complète
 */
export interface VoteCourrier {
  membreId: string;            // ID du membre CS ayant voté
  membreNom: string;           // Nom affiché pour historique
  statut: StatutVote;
  commentaire?: string;        // Obligatoire si statut = 'modification'
  dateVote: Timestamp;         // Date du vote (renommé depuis 'date' pour clarté)
  ip?: string;                 // Adresse IP pour traçabilité juridique
  juridiquementValide?: boolean; // Validation juridique du vote
}

/**
 * Alerte individuelle de l'analyse SCUD
 */
export interface AlerteSCUD {
  type: 'terme_risque' | 'accusation' | 'imperatif' | 'menace' | 'emotion' | 'juridique';
  message: string;
  extrait?: string;            // Passage concerné dans le courrier
  suggestion?: string;         // Reformulation suggérée
}

/**
 * Reformulation proposée par SENTINELLE - V2
 */
export interface ReformulationProposee {
  objet: string;
  contenu: string;
  genereLe?: Timestamp;
}

/**
 * Résultat de l'analyse SCUD par Sentinelle
 * Enrichi en Session 52 pour SENTINELLE V2
 * Enrichi en Session 92 pour Classification 3 niveaux
 */
export interface AnalyseSCUD {
  dateAnalyse?: Timestamp;
  scoreRisque: NiveauRisque;
  /** Nature de la communication - SENTINELLE V2 */
  nature?: NatureCommunication;
  /** Classification 3 niveaux - Gouvernance V2 (Session 92) */
  niveauClassification?: NiveauClassification;
  alertes: AlerteSCUD[];
  recommandations?: string[];
  analyseComplete?: string;     // Texte complet de l'analyse
  /** Mots-clés engageants détectés - SENTINELLE V2 */
  motsClesEngageants?: string[];
  /** Reformulation proposée si risque élevé - SENTINELLE V2 */
  reformulationProposee?: ReformulationProposee;
  /** L'utilisateur a-t-il appliqué la reformulation ? */
  reformulationAppliquee?: boolean;
}

/**
 * Pièce jointe d'un courrier
 */
export interface PieceJointeCourrier {
  id: string;
  nom: string;
  type: string;                // MIME type
  taille: number;              // en bytes
  storageUrl: string;          // URL Firebase Storage
  documentId?: string;         // Si lié à un document GED existant
}

/**
 * Relance manuelle effectuée par l'admin
 */
export interface RelanceManuelle {
  date: Timestamp;
  parUserId: string;
  parUserNom: string;
}

// ============================================================================
// ENTITÉ PRINCIPALE
// ============================================================================

/**
 * Courrier sortant
 * Document Firestore dans la collection 'courriers'
 */
export interface Courrier {
  id: string;
  coproprieteId: string;
  
  /**
   * Numéro unique du courrier
   * Format: COU-2026-001
   */
  numero: string;
  
  // --- Contenu ---
  
  objet: string;
  contenu: string;             // Corps du courrier (HTML ou texte)
  type: TypeCourrier;
  modeEnvoi: ModeEnvoi;
  
  // --- Destinataire ---
  
  destinataire: DestinataireCourrier;
  
  // --- Session 160 : Copie carbone ---
  
  /** Destinataires en copie (CC) — sélectionnés parmi les contacts ou saisis manuellement */
  destinatairesCC?: DestinataireCourrier[];
  
  // --- Pièces jointes ---
  
  piecesJointes: PieceJointeCourrier[];
  
  // --- Liens métier ---
  
  affaireId?: string;          // Rattachement optionnel à une affaire
  signalementIds?: string[];   // Rattachement optionnel à des signalements
  courrierReponseA?: string;   // ID du courrier/email auquel celui-ci répond
  
  // --- Session 143 : Correspondance entrante (archivage SENTINELLE) ---
  
  /** Sens de la correspondance : entrant (reçu) ou sortant (envoyé) */
  sens?: SensCourrier;
  /** ID du document GED associé (si archivé via SENTINELLE) */
  documentGedId?: string;
  /** Source de création : 'sentinelle_vigie', 'sentinelle_cs', 'manuel' */
  sourceCreation?: string;
  
  // --- Workflow ---
  
  statut: StatutCourrier;
  version: number;             // Incrémenté à chaque nouvelle version
  courrierParentId?: string;   // Si créé suite à demande de modification
  
  // --- Classification Gouvernance V2 (Session 92) ---
  
  /**
   * Niveau de classification déterminé par SENTINELLE
   * Peut être surclassé par le coordinateur (jamais déclassé)
   * Détermine le workflow : VERT/ORANGE = envoi direct, ROUGE = vote CS
   */
  niveauClassification?: NiveauClassification;
  
  /**
   * true si le coordinateur a surclassé manuellement le niveau
   */
  niveauSurclasse?: boolean;
  
  // --- Rédaction ---
  
  redacteurId: string;
  redacteurNom: string;
  dateCreation: Timestamp;
  dateModification: Timestamp;
  dateSoumission?: Timestamp;  // Quand passé en 'en_validation'
  
  // --- Validation ---
  
  /**
   * Votes des membres CS
   * Clé = userId du membre, Valeur = son vote
   */
  validations: {
    [membreId: string]: VoteCourrier;
  };
  
  /**
   * Nombre de votes requis
   * ROUGE : minimum 1 membre CS doit approuver
   * VERT/ORANGE : 0 (pas de vote requis)
   */
  votesRequis: number;
  
  /**
   * Date de validation unanime
   */
  dateValidation?: Timestamp;
  
  // --- Envoi ---
  
  dateEnvoi?: Timestamp;
  envoyePar?: string;          // userId de celui qui a cliqué "Envoyer"
  gmailMessageId?: string;     // ID du message Gmail si envoi email
  pdfUrl?: string;             // URL du PDF généré si envoi postal
  
  // --- Analyse Sentinelle ---
  
  analyseSentinelle?: AnalyseSCUD;

  // --- Session 126 : Flag modification manuelle ---
  
  /** true si le brouillon a été modifié par l'utilisateur après génération SENTINELLE */
  modifieParUtilisateur?: boolean;
  
  // --- Relances ---
  
  /**
   * Historique des relances manuelles par membre
   * Clé = membreId, Valeur = info relance
   */
  relancesManuelles?: {
    [membreId: string]: RelanceManuelle;
  };
  
  // --- Session 179 : Résolution transversale ---

  /**
   * Marque la correspondance comme résolue (sujet traité, réponse obtenue).
   * Distinct du statut workflow : un courrier envoyé reste 'envoye' mais le sujet
   * peut être résolu. Les éléments resolu=true sont exclus du contexte SENTINELLE.
   * Rétrocompatible : absent ou false = non résolu.
   */
  resolu?: boolean;

  // --- Métadonnées ---
  
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================================================
// DONNÉES POUR CRÉATION / MISE À JOUR
// ============================================================================

/**
 * Données pour créer un nouveau courrier (brouillon)
 * Enrichi en Session 52 pour SENTINELLE V2
 * Enrichi en Session 92 pour Classification 3 niveaux
 */
export interface CourrierCreateData {
  coproprieteId: string;
  objet: string;
  contenu: string;
  type: TypeCourrier;
  modeEnvoi: ModeEnvoi;
  destinataire: DestinataireCourrier;
  destinatairesCC?: DestinataireCourrier[];  // Session 160 : copie carbone
  piecesJointes?: Omit<PieceJointeCourrier, 'id'>[];
  affaireId?: string;
  signalementIds?: string[];
  courrierReponseA?: string;
  redacteurId: string;
  redacteurNom: string;
  /** Analyse SENTINELLE pré-remplie - V2 */
  analyseSentinelle?: {
    risque: NiveauRisque;
    nature: NatureCommunication;
    /** Classification 3 niveaux - Session 92 */
    niveauClassification?: NiveauClassification;
  };
}

/**
 * Données pour mettre à jour un courrier (brouillon uniquement)
 */
export interface CourrierUpdateData {
  objet?: string;
  contenu?: string;
  type?: TypeCourrier;
  modeEnvoi?: ModeEnvoi;
  destinataire?: DestinataireCourrier;
  destinatairesCC?: DestinataireCourrier[];  // Session 160 : copie carbone
  piecesJointes?: PieceJointeCourrier[];
  affaireId?: string;
  signalementIds?: string[];
  modifieParUtilisateur?: boolean; // Session 126
}

/**
 * Données pour soumettre un vote
 * Session 81 : ajout ip et juridiquementValide pour traçabilité
 */
export interface VoteData {
  courrierId: string;
  membreId: string;
  membreNom: string;
  statut: StatutVote;
  commentaire?: string;
  ip?: string;
  juridiquementValide?: boolean;
}

// ============================================================================
// LABELS ET CONSTANTES
// ============================================================================

export const STATUT_COURRIER_LABELS: Record<StatutCourrier, string> = {
  brouillon: 'Brouillon',
  en_validation: 'En validation',
  valide: 'Validé',
  envoye: 'Envoyé',
  recu: 'Reçu',
  annule: 'Annulé',
  rejete: 'Rejeté'
};

export const STATUT_COURRIER_COLORS: Record<StatutCourrier, string> = {
  brouillon: 'bg-gray-100 text-gray-800',
  en_validation: 'bg-blue-100 text-blue-800',
  valide: 'bg-green-100 text-green-800',
  envoye: 'bg-emerald-100 text-emerald-800',
  recu: 'bg-cyan-100 text-cyan-800',
  annule: 'bg-orange-100 text-orange-800',
  rejete: 'bg-red-100 text-red-800'
};

export const TYPE_COURRIER_LABELS: Record<TypeCourrier, string> = {
  mise_en_demeure: 'Mise en demeure',
  relance: 'Relance',
  demande_information: 'Demande d\'information',
  notification: 'Notification',
  reclamation: 'Réclamation',
  convocation: 'Convocation',
  compte_rendu: 'Compte-rendu',
  autre: 'Autre'
};

export const MODE_ENVOI_LABELS: Record<ModeEnvoi, string> = {
  email: 'Email',
  postal: 'Courrier postal simple',
  lrar: 'Lettre recommandée AR',
  recommande_ar: 'Recommandé avec AR'
};

export const STATUT_VOTE_LABELS: Record<StatutVote, string> = {
  approuve: 'Approuvé',
  modification: 'Modification demandée',
  rejete: 'Rejeté'
};

export const STATUT_VOTE_COLORS: Record<StatutVote, string> = {
  approuve: 'bg-green-100 text-green-800',
  modification: 'bg-amber-100 text-amber-800',
  rejete: 'bg-red-100 text-red-800'
};

export const NIVEAU_RISQUE_LABELS: Record<NiveauRisque, string> = {
  faible: 'Risque faible',
  moyen: 'Risque moyen',
  eleve: 'Risque élevé'
};

export const NIVEAU_RISQUE_COLORS: Record<NiveauRisque, string> = {
  faible: 'bg-green-100 text-green-800',
  moyen: 'bg-amber-100 text-amber-800',
  eleve: 'bg-red-100 text-red-800'
};

export const NATURE_COMMUNICATION_LABELS: Record<NatureCommunication, string> = {
  courante: 'Courante',
  engageante: 'Engageante'
};

export const NATURE_COMMUNICATION_COLORS: Record<NatureCommunication, string> = {
  courante: 'bg-blue-100 text-blue-800',
  engageante: 'bg-purple-100 text-purple-800'
};

// ============================================================================
// CLASSIFICATION 3 NIVEAUX - GOUVERNANCE V2 (Session 92)
// ============================================================================

export const NIVEAU_CLASSIFICATION_LABELS: Record<NiveauClassification, string> = {
  vert: 'Vert — Envoi libre',
  orange: 'Orange — Envoi libre',
  rouge: 'Rouge — Vote CS requis'
};

export const NIVEAU_CLASSIFICATION_SHORT_LABELS: Record<NiveauClassification, string> = {
  vert: 'VERT',
  orange: 'ORANGE',
  rouge: 'ROUGE'
};

export const NIVEAU_CLASSIFICATION_COLORS: Record<NiveauClassification, string> = {
  vert: 'bg-green-100 text-green-800 border-green-300',
  orange: 'bg-orange-100 text-orange-800 border-orange-300',
  rouge: 'bg-red-100 text-red-800 border-red-300'
};

export const NIVEAU_CLASSIFICATION_DOT_COLORS: Record<NiveauClassification, string> = {
  vert: 'bg-green-500',
  orange: 'bg-orange-500',
  rouge: 'bg-red-500'
};

export const NIVEAU_CLASSIFICATION_DESCRIPTIONS: Record<NiveauClassification, string> = {
  vert: 'Information, demande de document, relance simple. Le coordinateur peut envoyer directement.',
  orange: 'Réclamation, contestation, mise en demeure amiable. Le coordinateur peut envoyer directement.',
  rouge: 'Engagement juridique, menace de procédure. Au moins 1 membre du conseil syndical doit approuver avant envoi.'
};

/**
 * Ordre hiérarchique des niveaux (pour surclassement)
 * Plus le nombre est élevé, plus le niveau est restrictif
 */
export const NIVEAU_CLASSIFICATION_ORDRE: Record<NiveauClassification, number> = {
  vert: 1,
  orange: 2,
  rouge: 3
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Vérifie si un courrier peut être modifié
 */
export function peutModifier(courrier: Courrier): boolean {
  return courrier.statut === 'brouillon';
}

/**
 * Vérifie si un courrier peut être soumis à validation
 */
export function peutSoumettre(courrier: Courrier): boolean {
  return courrier.statut === 'brouillon' 
    && courrier.objet.trim() !== '' 
    && courrier.contenu.trim() !== '';
}

/**
 * Vérifie si un courrier a obtenu l'unanimité
 * Session 92 : Pour ROUGE, il faut au minimum 1 approbation (pas unanimité)
 */
export function aUnanimite(courrier: Courrier): boolean {
  const votes = Object.values(courrier.validations);
  if (votes.length < courrier.votesRequis) return false;
  return votes.every(v => v.statut === 'approuve');
}

/**
 * Session 92 : Vérifie si un courrier ROUGE a au moins 1 approbation
 * Condition de validation pour les courriers ROUGE (Gouvernance V2)
 */
export function aApprobationMinimale(courrier: Courrier): boolean {
  const votes = Object.values(courrier.validations);
  return votes.some(v => v.statut === 'approuve');
}

/**
 * Vérifie si un courrier a été rejeté
 */
export function estRejete(courrier: Courrier): boolean {
  const votes = Object.values(courrier.validations);
  return votes.some(v => v.statut === 'rejete');
}

/**
 * Vérifie si une modification a été demandée
 */
export function modificationDemandee(courrier: Courrier): boolean {
  const votes = Object.values(courrier.validations);
  return votes.some(v => v.statut === 'modification');
}

/**
 * Compte le nombre de votes par statut
 */
export function compterVotes(courrier: Courrier): Record<StatutVote, number> {
  const votes = Object.values(courrier.validations);
  return {
    approuve: votes.filter(v => v.statut === 'approuve').length,
    modification: votes.filter(v => v.statut === 'modification').length,
    rejete: votes.filter(v => v.statut === 'rejete').length
  };
}

/**
 * Vérifie si un membre a déjà voté
 */
export function aVote(courrier: Courrier, membreId: string): boolean {
  return courrier.validations[membreId] !== undefined;
}

/**
 * Récupère le vote d'un membre
 */
export function getVote(courrier: Courrier, membreId: string): VoteCourrier | undefined {
  return courrier.validations[membreId];
}

/**
 * Vérifie si un courrier peut être envoyé
 */
export function peutEnvoyer(courrier: Courrier): boolean {
  return courrier.statut === 'valide';
}

/**
 * Génère la signature collective du CS
 */
export function getSignatureCollective(nomCopropriete: string): string {
  return `Le Conseil Syndical de la ${nomCopropriete}`;
}

// ============================================================================
// CYCLE DE VIE COURRIER — STATUT AFFICHÉ (Session 180)
// ============================================================================

/**
 * Statut affiché à l'utilisateur, calculé à la volée.
 * Ne remplace PAS StatutCourrier en base Firestore.
 * 
 * Cycle de vie d'un courrier sortant :
 *   envoye → en_attente → delai_depasse → repondu → clos
 * 
 * Calcul :
 *   - clos          : resolu === true
 *   - repondu       : au moins un courrier entrant lié (courrierReponseA pointe vers ce courrier)
 *   - delai_depasse : envoyé + délai de réponse dépassé + pas de réponse
 *   - en_attente    : envoyé + délai de réponse en cours + pas de réponse
 *   - sinon         : le statut Firestore brut (brouillon, envoye, recu, etc.)
 */
export type StatutCourrierAffiche =
  | StatutCourrier
  | 'en_attente'
  | 'delai_depasse'
  | 'repondu'
  | 'clos';

export const STATUT_AFFICHE_LABELS: Record<StatutCourrierAffiche, string> = {
  brouillon: 'Brouillon',
  en_validation: 'En validation',
  valide: 'Validé',
  envoye: 'Envoyé',
  recu: 'Reçu',
  annule: 'Annulé',
  rejete: 'Rejeté',
  en_attente: 'En attente de réponse',
  delai_depasse: 'Délai dépassé',
  repondu: 'Réponse reçue',
  clos: 'Clos',
};

export const STATUT_AFFICHE_COLORS: Record<StatutCourrierAffiche, string> = {
  brouillon: 'bg-gray-100 text-gray-800',
  en_validation: 'bg-blue-100 text-blue-800',
  valide: 'bg-green-100 text-green-800',
  envoye: 'bg-emerald-100 text-emerald-800',
  recu: 'bg-cyan-100 text-cyan-800',
  annule: 'bg-orange-100 text-orange-800',
  rejete: 'bg-red-100 text-red-800',
  en_attente: 'bg-amber-100 text-amber-800',
  delai_depasse: 'bg-red-100 text-red-800',
  repondu: 'bg-blue-100 text-blue-800',
  clos: 'bg-gray-100 text-gray-500',
};

/**
 * Calcule le statut affiché d'un courrier à partir de ses données Firestore.
 * Aucune écriture en base — calcul pur à l'affichage.
 * 
 * @param courrier - Le courrier (ou un objet partiel avec les champs nécessaires)
 * @param options  - aReponse: true si un courrier entrant lié existe
 *                   delaiJours: délai de réponse en jours (défaut: 7 pour sortants envoyés)
 */
export function getStatutAffiche(
  courrier: {
    statut: StatutCourrier;
    sens?: SensCourrier;
    resolu?: boolean;
    dateEnvoi?: Timestamp | null;
  },
  options?: { aReponse?: boolean; delaiJours?: number }
): StatutCourrierAffiche {
  // Courrier entrant → pas de cycle de vie sortant
  if (courrier.sens === 'entrant' || courrier.statut === 'recu') {
    return courrier.resolu ? 'clos' : courrier.statut;
  }

  // Clos — résolu manuellement par l'utilisateur
  if (courrier.resolu) {
    return 'clos';
  }

  // Pas envoyé → statut brut
  if (courrier.statut !== 'envoye') {
    return courrier.statut;
  }

  // À partir d'ici : courrier sortant envoyé

  // Réponse reçue (courrier entrant lié détecté par l'appelant)
  if (options?.aReponse) {
    return 'repondu';
  }

  // Pas de date d'envoi → reste "envoyé"
  if (!courrier.dateEnvoi) {
    return 'envoye';
  }

  // Calcul du délai (défaut 7 jours — délai raisonnable standard)
  const delai = options?.delaiJours ?? 7;
  const dateEnvoi = courrier.dateEnvoi.toDate ? courrier.dateEnvoi.toDate() : new Date(courrier.dateEnvoi as any);
  const dateLimite = new Date(dateEnvoi);
  dateLimite.setDate(dateLimite.getDate() + delai);
  const maintenant = new Date();

  if (maintenant > dateLimite) {
    return 'delai_depasse';
  }

  return 'en_attente';
}

// ============================================================================
// HELPERS CLASSIFICATION 3 NIVEAUX - GOUVERNANCE V2 (Session 92)
// ============================================================================

/**
 * Détermine le niveau de classification à partir de l'analyse SCUD
 * 
 * Règles de mapping :
 * - Risque faible + nature courante → VERT
 * - Risque moyen OU (risque faible + réclamation/contestation) → ORANGE  
 * - Risque élevé OU nature engageante (termes juridiques) → ROUGE
 * 
 * La présence de mots-clés engageants force toujours ROUGE
 */
export function determinerNiveauClassification(
  scoreRisque: NiveauRisque,
  nature?: NatureCommunication,
  motsClesEngageants?: string[]
): NiveauClassification {
  // Mots-clés engageants détectés → toujours ROUGE
  if (motsClesEngageants && motsClesEngageants.length > 0) {
    return 'rouge';
  }
  
  // Nature engageante → toujours ROUGE
  if (nature === 'engageante') {
    return 'rouge';
  }
  
  // Mapping scoreRisque → niveau
  switch (scoreRisque) {
    case 'faible':
      return 'vert';
    case 'moyen':
      return 'orange';
    case 'eleve':
      return 'rouge';
    default:
      return 'orange'; // Par défaut, prudent
  }
}

/**
 * Vérifie si un courrier nécessite un vote du CS
 * Seul le niveau ROUGE requiert un vote
 */
export function necessiteVoteCS(niveau: NiveauClassification): boolean {
  return niveau === 'rouge';
}

/**
 * Vérifie si un surclassement est autorisé
 * On peut monter (VERT→ORANGE, ORANGE→ROUGE, VERT→ROUGE) mais jamais descendre
 * 
 * @returns true si le surclassement de niveauActuel vers niveauCible est autorisé
 */
export function peutSurclasser(
  niveauActuel: NiveauClassification,
  niveauCible: NiveauClassification
): boolean {
  return NIVEAU_CLASSIFICATION_ORDRE[niveauCible] > NIVEAU_CLASSIFICATION_ORDRE[niveauActuel];
}

/**
 * Retourne les niveaux vers lesquels le coordinateur peut surclasser
 * (niveaux supérieurs au niveau actuel uniquement)
 */
export function getNiveauxSurclassementPossibles(
  niveauActuel: NiveauClassification
): NiveauClassification[] {
  const ordreActuel = NIVEAU_CLASSIFICATION_ORDRE[niveauActuel];
  return (['vert', 'orange', 'rouge'] as NiveauClassification[]).filter(
    n => NIVEAU_CLASSIFICATION_ORDRE[n] > ordreActuel
  );
}

/**
 * Détermine le nombre de votes requis selon le niveau
 * VERT / ORANGE : 0 (pas de vote, envoi direct par coordinateur)
 * ROUGE : 1 minimum (au moins 1 membre CS doit approuver)
 */
export function getVotesRequisParNiveau(
  niveau: NiveauClassification,
  _nbMembresCS?: number
): number {
  if (niveau === 'rouge') {
    // Gouvernance V2 : minimum 1 approbation requise
    return 1;
  }
  // VERT et ORANGE : pas de vote requis
  return 0;
}
